/**
 * A live call session (plan 042): one `LiveCallStore`, the chats it can
 * delegate into, and the recap saved when the call ends. Framework-
 * agnostic; `useMagnusLiveCall` wraps it for SDK hosts, and the flagship
 * web and mobile apps build their providers on it.
 *
 * By default a call binds to the open conversation's (possibly still
 * client-minted) chat id and creates the row only at save time, when the
 * user actually spoke and no delegated turn materialized it. A host that
 * wants the flagship behaviour — a "Voice chat" row the moment the call
 * connects — returns a `create` from `bindChat`.
 */

import type { MagnusUIMessage } from "@magnus/contract/messages";
import { VOICE_CHAT_TITLE } from "@magnus/contract/values";
import type {
  SdkLiveVoiceAvailability,
  SdkLiveVoiceUnavailableReason,
} from "@magnus/contract/voice";
import {
  DEFAULT_LIVE_VOICE,
  isLiveVoice,
  LIVE_VOICES,
  type LiveVoice,
} from "@workspace/voice-shared/live";
import type {
  MagnusChatDelegate,
  MagnusChatOutcome,
} from "@workspace/voice-shared/magnus-chat-tool";
import type { MagnusClient } from "../../client";
import type { LiveCallChatBridge } from "./bridge";
import type { LiveCallPlatform } from "./platform";
import {
  defaultLiveCallStorage,
  flushVoiceSessions,
  type LiveCallStorage,
  saveVoiceSessionRecap,
  type VoiceSessionPost,
} from "./save";
import {
  consoleLiveCallLog,
  createLiveCallStore,
  LIVE_CALL_DISABLED_MESSAGE,
  LIVE_CALL_UNCONFIGURED_MESSAGE,
  type LiveCallEnd,
  type LiveCallMint,
  type LiveCallStartResult,
  type LiveCallState,
  type LiveCallStopOptions,
  type LiveCallStore,
} from "./store";

const LIVE_VOICE_STORAGE_KEY = "magnus.voice.live-voice";
const BRIDGE_WAIT_MS = 15_000;
const RECAP_IDLE_WAIT_MS = 60_000;

export const LIVE_CALL_SAVE_FAILED_MESSAGE =
  "Couldn’t save the voice chat. It will retry next time.";

export type LiveCallAvailability =
  | { status: "unknown" }
  | { status: "available" }
  | {
      status: "unavailable";
      /** `error` when the availability check itself failed (e.g. a server
       *  that predates live calls). */
      reason: SdkLiveVoiceUnavailableReason | "error";
    };

export type MagnusLiveCallState = {
  availability: LiveCallAvailability;
  call: LiveCallState;
  /** The chat the call delegates into, while one is live. */
  callChatId: string | null;
  /** False while a `bindChat` `create` is still creating that chat. */
  callChatReady: boolean;
  /** What to show the user: the call's own failure, or a refused start /
   *  failed save. Cleared on the next start. */
  error: string | null;
  /** With no call bound, the chat `error` belongs in: the one a refused
   *  start came from, or whose recap didn't save. Null: any chat. */
  errorChatId: string | null;
  /** Whether the pill shows captions; persisted in `storage`. */
  captionsOn: boolean;
  /** Persisted in `storage`. */
  voice: LiveVoice;
};

/** How the controller reaches the server. */
export type LiveCallTransport = {
  /** Absent: calls are assumed available (the host gates them itself). */
  availability?: () => Promise<SdkLiveVoiceAvailability>;
  /** Creates a never-sent chat at save time, when the user spoke. */
  createChat: (input: { id: string; title: string }) => Promise<unknown>;
  mint: LiveCallMint;
  /** Frees the server's one-call-per-user lease. */
  release: (
    callSessionId: string,
    options: { keepalive: boolean }
  ) => Promise<void>;
  save: VoiceSessionPost;
};

/** Everything over the versioned SDK REST surface. */
export function magnusClientTransport(client: MagnusClient): LiveCallTransport {
  return {
    availability: () => client.voice.liveAvailability(),
    createChat: (input) => client.chats.create(input),
    mint: (input, options) => client.voice.liveSession(input, options),
    release: (callSessionId, options) =>
      client.voice.endLiveSession(callSessionId, options),
    save: (chatId, body, keepalive) =>
      client.chats.saveVoiceSession(chatId, body, { keepalive }),
  };
}

/** The chat a new call delegates into. */
export type LiveCallBinding = {
  chatId: string;
  /** Creates `chatId` once the call connects; resolving false ends the
   *  call. The recap then asks the server to delete the chat again if the
   *  user said nothing (`discardIfEmpty`). */
  create?: () => Promise<boolean>;
};

export type LiveCallRecapSaved = {
  message: MagnusUIMessage | null;
  discarded: boolean;
  saved: boolean;
  /** A chat for it was mounted when the save settled. */
  mounted: boolean;
};

export type MagnusLiveCallOptions<
  B extends LiveCallChatBridge = LiveCallChatBridge,
> = {
  /** Picks the chat a new call binds to; null refuses the start. Default:
   *  the newest registered chat. */
  bindChat?: (open: B | null) => LiveCallBinding | null;
  /** The call ended (before its recap is saved). */
  onCallEnd?: (end: LiveCallEnd) => void;
  /** The recap save created the chat row (no delegated turn had). */
  onChatMaterialized?: (chatId: string) => void;
  /** A delegated turn settled in `chatId`. */
  onDelegated?: (chatId: string) => void;
  /** A recap was saved (or discarded) — refresh any chat list. */
  onRecapSaved?: (chatId: string, saved: LiveCallRecapSaved) => void;
  /** Open a chat the call is bound to but which isn't mounted (the user
   *  navigated away mid-call), so a delegated turn has somewhere to land. */
  openChat?: (chatId: string) => void;
  /** Default: shown as `error` until the next start. */
  reportSaveFailure?: (message: string) => void;
};

export interface MagnusLiveCallController<
  B extends LiveCallChatBridge = LiveCallChatBridge,
> {
  dispose(): void;
  /** Ends the call bound to a chat that no longer exists, without a recap. */
  dropChat(chatId: string): void;
  getState(): MagnusLiveCallState;
  pushContext(notes: readonly string[]): void;
  /** Make a mounted chat a delegation target; the newest registration is
   *  the one a new call binds to. Returns the unregister. */
  registerChat(bridge: B): () => void;
  setCaptionsOn(on: boolean): void;
  setInputDevice(deviceId: string | null): Promise<void>;
  setMuted(muted: boolean): void;
  setOptions(options: MagnusLiveCallOptions<B>): void;
  setVoice(voice: LiveVoice): void;
  start(): Promise<LiveCallStartResult>;
  stop(options?: Pick<LiveCallStopOptions, "reason">): void;
  subscribe(listener: () => void): () => void;
  readonly voices: typeof LIVE_VOICES;
}

const CAPTIONS_STORAGE_KEY = "magnus.voice.captions";

function readPref(
  storage: LiveCallStorage | undefined,
  key: string,
  apply: (value: string | null) => void
) {
  try {
    const value = storage?.getItem(key) ?? null;
    if (value instanceof Promise) {
      value.then(apply, () => undefined);
    } else {
      apply(value);
    }
  } catch {
    // storage unavailable; keep the default
  }
}

function writePref(
  storage: LiveCallStorage | undefined,
  key: string,
  value: string
) {
  try {
    Promise.resolve(storage?.setItem(key, value)).catch(() => undefined);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
}

export function localRecap(end: LiveCallEnd): MagnusUIMessage {
  const durationMs = Math.max(
    0,
    Date.parse(end.endedAt) - Date.parse(end.startedAt)
  );
  return {
    id: `voice-${end.sessionId}`,
    role: "assistant",
    parts: [
      {
        type: "data-voice-session",
        data: {
          sessionId: end.sessionId,
          startedAt: end.startedAt,
          endedAt: end.endedAt,
          durationMs: Number.isFinite(durationMs) ? durationMs : 0,
          endReason: end.endReason,
          turns: end.turns,
          ...(end.callId ? { callId: end.callId } : {}),
        },
      },
    ],
    metadata: { voiceSessionId: end.sessionId },
  };
}

function withTimeout(promise: Promise<void>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

const canWatchPageHide = () =>
  typeof window !== "undefined" &&
  typeof window.addEventListener === "function";

export function createMagnusLiveCall<
  B extends LiveCallChatBridge = LiveCallChatBridge,
>(
  config: (
    | { client: MagnusClient; transport?: undefined }
    | { client?: undefined; transport: LiveCallTransport }
  ) & {
    options?: MagnusLiveCallOptions<B>;
    /** Where prefs and unsaved recaps live; default `localStorage`. */
    storage?: LiveCallStorage;
    /** Only its `log` is used when `store` is given. */
    platform?: LiveCallPlatform;
    /** Inject for tests; defaults to a store minting through the transport. */
    store?: LiveCallStore;
  }
): MagnusLiveCallController<B> {
  const transport = config.transport ?? magnusClientTransport(config.client);
  const storage = config.storage ?? defaultLiveCallStorage();
  const log = config.platform?.log ?? consoleLiveCallLog;
  let options: MagnusLiveCallOptions<B> = config.options ?? {};
  const store =
    config.store ??
    createLiveCallStore({ mint: transport.mint, platform: config.platform });

  let bridges: B[] = [];
  let bridgeWaiters: Array<(bridge: B) => void> = [];
  let boundChatId: string | null = null;
  let starting = false;
  let pendingCreate: (() => Promise<boolean>) | null = null;
  let createdChatId: string | null = null;
  let droppedChatId: string | null = null;
  let chatReady: Promise<boolean> = Promise.resolve(true);

  let state: MagnusLiveCallState = {
    availability: { status: "unknown" },
    call: store.getState(),
    callChatId: null,
    callChatReady: true,
    captionsOn: false,
    error: null,
    errorChatId: null,
    voice: DEFAULT_LIVE_VOICE,
  };
  let notice: string | null = null;
  let noticeChatId: string | null = null;
  const listeners = new Set<() => void>();
  // A pref set before async storage answers wins over the stored one.
  const prefsChosen = new Set<string>();
  const emit = () => {
    for (const listener of [...listeners]) {
      listener();
    }
  };
  const update = (
    patch: Partial<Omit<MagnusLiveCallState, "error" | "errorChatId">>
  ) => {
    const next = { ...state, ...patch };
    next.error = notice ?? next.call.error;
    next.errorChatId = notice ? noticeChatId : null;
    state = next;
    emit();
  };
  const setNotice = (message: string | null, chatId: string | null = null) => {
    notice = message;
    noticeChatId = chatId;
    update({});
  };
  readPref(storage, LIVE_VOICE_STORAGE_KEY, (stored) => {
    if (
      isLiveVoice(stored) &&
      !prefsChosen.has(LIVE_VOICE_STORAGE_KEY) &&
      state.call.phase === "idle"
    ) {
      update({ voice: stored });
    }
  });
  readPref(storage, CAPTIONS_STORAGE_KEY, (stored) => {
    if (!prefsChosen.has(CAPTIONS_STORAGE_KEY)) {
      update({ captionsOn: stored === "on" });
    }
  });

  const reportSaveFailure = (message: string, chatId: string | null) => {
    if (options.reportSaveFailure) {
      options.reportSaveFailure(message);
    } else {
      setNotice(message, chatId && bridgeFor(chatId) ? chatId : null);
    }
  };

  const bridgeFor = (chatId: string) =>
    bridges.findLast((b) => b.chatId === chatId) ?? null;

  const waitForBridge = (chatId: string, signal: AbortSignal) =>
    new Promise<B | null>((resolve) => {
      const timer = setTimeout(() => finish(null), BRIDGE_WAIT_MS);
      const finish = (bridge: B | null) => {
        if (bridge && bridge.chatId !== chatId) {
          return;
        }
        clearTimeout(timer);
        bridgeWaiters = bridgeWaiters.filter((w) => w !== finish);
        resolve(bridge);
      };
      signal.addEventListener("abort", () => finish(null), { once: true });
      bridgeWaiters.push(finish);
    });

  const delegate: MagnusChatDelegate = async (
    query,
    signal,
    callContext
  ): Promise<MagnusChatOutcome> => {
    const chatId = boundChatId;
    if (!chatId) {
      return { kind: "failed", message: "the call has no chat" };
    }
    if (createdChatId === chatId && !(await chatReady)) {
      return { kind: "failed", message: "the chat couldn't be created" };
    }
    let bridge = bridgeFor(chatId);
    if (!bridge) {
      const waiting = waitForBridge(chatId, signal);
      options.openChat?.(chatId);
      bridge = await waiting;
    }
    if (!bridge) {
      return { kind: "failed", message: "the chat didn't open" };
    }
    const outcome = await bridge.delegate(query, signal, callContext);
    options.onDelegated?.(chatId);
    return outcome;
  };

  const createBoundChat = () => {
    const create = pendingCreate;
    const chatId = boundChatId;
    pendingCreate = null;
    if (!(create && chatId)) {
      return;
    }
    createdChatId = chatId;
    update({ callChatReady: false });
    chatReady = create()
      .catch(() => false)
      .then((ok) => {
        if (boundChatId === chatId) {
          if (ok) {
            update({ callChatReady: true });
          } else {
            boundChatId = null;
            createdChatId = null;
            update({ callChatId: null, callChatReady: true });
            store.stop();
          }
        }
        return ok;
      });
  };

  const persist = async (end: LiveCallEnd) => {
    const chatId = boundChatId;
    const created = chatId !== null && createdChatId === chatId;
    const dropped = chatId !== null && droppedChatId === chatId;
    const ready = chatReady;
    boundChatId = null;
    createdChatId = null;
    droppedChatId = null;
    chatReady = Promise.resolve(true);
    update({ callChatId: null, callChatReady: true });
    if (
      !chatId ||
      dropped ||
      (end.turns.length === 0 && !created) ||
      !(await ready)
    ) {
      return;
    }
    const spoke = end.turns.some((t) => t.role === "user");
    const optimistic =
      !end.unloading && (!created || spoke) ? localRecap(end) : null;
    const bridge = bridgeFor(chatId);
    if (bridge && !end.unloading) {
      await withTimeout(bridge.whenIdle(), RECAP_IDLE_WAIT_MS);
      if (optimistic) {
        bridge.upsertRecap(optimistic);
      }
    }
    const body = {
      sessionId: end.sessionId,
      startedAt: end.startedAt,
      endedAt: end.endedAt,
      endReason: end.endReason,
      turns: end.turns,
      ...(end.callId ? { callId: end.callId } : {}),
      ...(created ? { discardIfEmpty: true } : {}),
    };
    let outcome = await saveVoiceSessionRecap(transport.save, chatId, body, {
      keepalive: end.unloading,
      storage,
    });
    if (end.unloading) {
      return;
    }
    let materialized = false;
    if (!(created || outcome.saved) && outcome.status === 404 && spoke) {
      try {
        await transport.createChat({ id: chatId, title: VOICE_CHAT_TITLE });
        materialized = true;
        options.onChatMaterialized?.(chatId);
        outcome = await saveVoiceSessionRecap(transport.save, chatId, body, {
          storage,
        });
      } catch {
        // falls through to the unsaved branch below
      }
    }
    const mounted = bridgeFor(chatId);
    if (outcome.message) {
      mounted?.upsertRecap(outcome.message);
    } else if (
      optimistic &&
      (outcome.discarded || (outcome.status === 404 && !materialized))
    ) {
      mounted?.removeRecap(optimistic.id);
    }
    if (!outcome.saved && !(outcome.status === 404 && !spoke)) {
      reportSaveFailure(LIVE_CALL_SAVE_FAILED_MESSAGE, chatId);
    }
    options.onRecapSaved?.(chatId, {
      message: outcome.message,
      discarded: outcome.discarded,
      saved: outcome.saved,
      mounted: mounted !== null,
    });
  };

  store.setHandlers({
    delegate,
    onReady: createBoundChat,
    onCallEnd: (end) => {
      pendingCreate = null;
      transport
        .release(end.sessionId, { keepalive: end.unloading })
        .catch(() => undefined);
      options.onCallEnd?.(end);
      const chatId = boundChatId;
      persist(end).catch((err: unknown) => {
        log("error", "recap save failed", err);
        if (!end.unloading) {
          reportSaveFailure(LIVE_CALL_SAVE_FAILED_MESSAGE, chatId);
        }
      });
    },
    onCancel: () => {
      if (boundChatId) {
        bridgeFor(boundChatId)?.stopTurn();
      }
    },
    onLateSession: (callSessionId) => {
      transport
        .release(callSessionId, { keepalive: false })
        .catch(() => undefined);
    },
  });

  let unsubscribeStore: (() => void) | null = null;
  const watchStore = (watch: boolean) => {
    if (watch && !unsubscribeStore) {
      unsubscribeStore = store.subscribe(() =>
        update({ call: store.getState() })
      );
      if (state.call !== store.getState()) {
        update({ call: store.getState() });
      }
    } else if (!watch && unsubscribeStore) {
      unsubscribeStore();
      unsubscribeStore = null;
    }
  };

  let checked = false;
  const checkAvailability = () => {
    if (checked) {
      return;
    }
    checked = true;
    flushVoiceSessions(transport.save, storage).catch(() => undefined);
    if (!transport.availability) {
      update({ availability: { status: "available" } });
      return;
    }
    transport
      .availability()
      .then((result) =>
        update({
          availability: result.available
            ? { status: "available" }
            : { status: "unavailable", reason: result.reason ?? "disabled" },
        })
      )
      .catch(() =>
        update({ availability: { status: "unavailable", reason: "error" } })
      );
  };

  const onPageHide = () => store.stop({ unloading: true });
  let watchingPageHide = false;
  const watchPageHide = (watch: boolean) => {
    if (!canWatchPageHide() || watch === watchingPageHide) {
      return;
    }
    watchingPageHide = watch;
    if (watch) {
      window.addEventListener("pagehide", onPageHide);
    } else {
      window.removeEventListener("pagehide", onPageHide);
    }
  };

  const start = async (): Promise<LiveCallStartResult> => {
    if (starting || boundChatId) {
      return "started";
    }
    const open = bridges.at(-1) ?? null;
    const binding = options.bindChat
      ? options.bindChat(open)
      : open && { chatId: open.chatId };
    if (!binding) {
      return "failed";
    }
    starting = true;
    notice = null;
    try {
      boundChatId = binding.chatId;
      pendingCreate = binding.create ?? null;
      chatReady = Promise.resolve(true);
      update({ callChatId: binding.chatId, callChatReady: true });
      const result = await store.start({
        voice: state.voice,
        chatId: binding.chatId,
      });
      if (result === "disabled" || result === "unconfigured") {
        boundChatId = null;
        notice =
          result === "disabled"
            ? LIVE_CALL_DISABLED_MESSAGE
            : LIVE_CALL_UNCONFIGURED_MESSAGE;
        noticeChatId = open?.chatId ?? null;
        update({
          callChatId: null,
          availability: { status: "unavailable", reason: result },
        });
      }
      return result;
    } finally {
      starting = false;
    }
  };

  return {
    dispose: () => {
      store.dispose();
      watchPageHide(false);
    },
    dropChat: (chatId) => {
      if (boundChatId === chatId) {
        droppedChatId = chatId;
        store.stop();
      }
    },
    getState: () => state,
    pushContext: store.pushContext,
    registerChat: (bridge) => {
      bridges = [...bridges.filter((b) => b !== bridge), bridge];
      for (const resolve of bridgeWaiters.slice()) {
        resolve(bridge);
      }
      return () => {
        bridges = bridges.filter((b) => b !== bridge);
      };
    },
    setInputDevice: store.setInputDevice,
    setMuted: store.setMuted,
    setOptions: (next) => {
      options = next;
    },
    setCaptionsOn: (on) => {
      prefsChosen.add(CAPTIONS_STORAGE_KEY);
      writePref(storage, CAPTIONS_STORAGE_KEY, on ? "on" : "off");
      update({ captionsOn: on });
    },
    setVoice: (voice) => {
      prefsChosen.add(LIVE_VOICE_STORAGE_KEY);
      writePref(storage, LIVE_VOICE_STORAGE_KEY, voice);
      update({ voice });
      store.switchVoice(voice).catch(() => undefined);
    },
    start,
    stop: (stopOptions) => {
      if (notice) {
        setNotice(null);
      }
      store.stop(stopOptions);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      watchStore(true);
      checkAvailability();
      watchPageHide(true);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          watchStore(false);
          watchPageHide(false);
        }
      };
    },
    voices: LIVE_VOICES,
  };
}
